import { Request, CreateRequestData, UpdateRequestData } from '../types';
import { preserveRepeatableConfigurationsFromCachedRequest } from './createRequestRepeatableSectionFlush';
import {
  flushAndRewriteRichHtmlRecord,
  flushAndRewriteRichHtmlRecordForCreate,
  parseNumericRequestId,
  rewritePreviewSrcsInRecord,
  StagedInlineImageFlushError,
} from './flushOfflineInlineImageQueue';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { RequestsService } from './requestsService';
import {
  TEXT_AREA_TRANSPORT_FIELD_KEYS,
  prepareTextAreaFieldsForTransport,
} from '../utils/richContent/sanitizeTextArea';

/**
 * Offline-aware requests service
 * Provides the same interface as RequestsService but with offline support
 */
class OfflineRequestsService {
  private async prepareForTransport<T extends Record<string, unknown>>(
    data: T,
    requestId?: string | number | null,
  ): Promise<T> {
    const numericRequestId = parseNumericRequestId(requestId ?? null);
    const isConnected = networkService.isConnected();
    let prepared = data;

    if (isConnected) {
      prepared = await flushAndRewriteRichHtmlRecord(
        prepared,
        numericRequestId,
      );
    }

    return prepareTextAreaFieldsForTransport(prepared, {
      allowPendingAttachmentRefs: !isConnected,
      stripPendingAttachmentRefs: isConnected && numericRequestId == null,
    });
  }

  /**
   * Get all requests - returns cached data + offline requests, live data when online
   */
  async getAllRequests(): Promise<Request[]> {
    let serverRequests: Request[] = [];

    if (networkService.isConnected()) {
      try {
        // Online - fetch from server and update cache
        serverRequests = await RequestsService.getAllRequests();
        await offlineCache.cacheRequests(serverRequests);
      } catch (error) {
        console.warn('Failed to fetch from server, falling back to cache');
        // Fall back to cache if server request fails
        const { requests } = await offlineCache.getCachedRequests();
        serverRequests = requests;
      }
    } else {
      // Offline - return cached data
      const { requests } = await offlineCache.getCachedRequests();
      serverRequests = requests;
    }

    // Always include offline requests at the top of the list
    const offlineRequests = await offlineCache.getPendingOfflineRequests();

    const offlineAsRequests: Request[] = offlineRequests.map(offline => ({
      id: offline.id,
      customerName: offline.data.customerName,
      requestorName: offline.data.requestorName,
      region: offline.data.region,
      requestType: offline.data.requestType,
      stage: offline.data.stage || 'Initial',
      description: offline.data.description,
      additionalDetails: offline.data.additionalDetails || '',
      dateCreated: new Date(offline.timestamp).toISOString(),
      modifiedOn: new Date(offline.timestamp).toISOString(),
      modifiedBy: 'Offline Queue',
    }));

    // Return offline requests first, then server requests
    const combinedResults = [...offlineAsRequests, ...serverRequests];
    return combinedResults;
  }

  /**
   * Get request by ID - prioritizes fresh server data when online
   * Now supports offline request IDs for viewing/editing before sync
   */
  async getRequestById(id: string): Promise<Request | null> {
    // Check if it's an offline request ID
    if (id.startsWith('o-')) {
      const offlineRequest = await offlineCache.getOfflineRequestById(id);

      if (!offlineRequest) {
        console.warn(`Offline request ${id} not found in queue`);
        return null;
      }

      // Convert OfflineRequest to Request format for UI consumption
      const request: Request = {
        ...offlineRequest.data,
        id: offlineRequest.id,
        customerName: offlineRequest.data.customerName,
        requestorName: offlineRequest.data.requestorName,
        region: offlineRequest.data.region,
        requestType: offlineRequest.data.requestType,
        stage: offlineRequest.data.stage || 'Initial',
        description: offlineRequest.data.description,
        additionalDetails: offlineRequest.data.additionalDetails || '',
        dateCreated: new Date(offlineRequest.timestamp).toISOString(),
        modifiedOn: new Date(offlineRequest.timestamp).toISOString(),
        modifiedBy: 'Offline (Not Synced)',
      };

      return request;
    }

    // If online, try server first for fresh data
    if (networkService.isConnected()) {
      try {
        const serverRequest = await RequestsService.getRequestById(id);
        const cachedRequest = await offlineCache.getCachedRequestById(id);
        // Phase 1: reattach Facility Power feed rows when server GET omits them.
        const mergedRequest = preserveRepeatableConfigurationsFromCachedRequest(
          serverRequest as Record<string, unknown>,
          cachedRequest as Record<string, unknown> | null,
        ) as Request;
        // `upsertCachedRequest`, not `cacheRequests([one])`: the latter replaces the whole
        // cache key, so reading a single request evicted the other 49 and took their
        // deliverables bases with them. (MR !489 review, finding 01.)
        await offlineCache.upsertCachedRequest(mergedRequest);
        return mergedRequest;
      } catch (error) {
        console.error(`Failed to fetch request ${id} from server:`, error);
        // Fall back to cache if server fails
      }
    }

    // Fallback to cache if offline or server failed
    const cachedRequest = await offlineCache.getCachedRequestById(id);
    if (cachedRequest) {
      return cachedRequest;
    }

    // Final fallback: Check if this request exists in pending offline updates
    const offlineUpdates = await offlineCache.getOfflineUpdateQueue();

    if (offlineUpdates && Array.isArray(offlineUpdates)) {
      const pendingUpdate = offlineUpdates.find(
        update => update.requestId === id,
      );

      if (pendingUpdate) {
        console.warn(
          `Pending offline update for ${id} exists, but original request missing from cache`,
        );
        return null;
      }
    }

    console.warn(
      `Request ${id} not found in cache, offline queue, or update queue`,
    );
    return null;
  }

  /**
   * Helper to get the cache size limit
   */
  private async getCacheLimit(): Promise<number> {
    return 50; // MAX_CACHED_REQUESTS from offlineCache
  }

  /**
   * Upload staged inline images after the request exists, then PATCH rewritten HTML.
   * Used by Create Request, which calls `RequestsService.createRequest` directly.
   */
  async persistStagedInlineImagesAfterCreate(
    requestId: string | number,
    originalData: Record<string, unknown>,
    options?: { deferVersionCapture?: boolean },
  ): Promise<Request | null> {
    const numericRequestId = parseNumericRequestId(requestId);
    if (numericRequestId == null) {
      return null;
    }
    const withRefs = rewritePreviewSrcsInRecord(originalData);
    const { record: flushed, flush } =
      await flushAndRewriteRichHtmlRecordForCreate(withRefs, numericRequestId);

    if (flush.stagedBeforeFlush > 0) {
      if (flush.uploadedCount === 0 || flush.failedClientIds.length > 0) {
        throw new StagedInlineImageFlushError(flush.failedClientIds);
      }
    }

    const flushedPrepared = prepareTextAreaFieldsForTransport(flushed, {
      stripPendingAttachmentRefs: true,
    });
    const strippedPrepared = prepareTextAreaFieldsForTransport(withRefs, {
      stripPendingAttachmentRefs: true,
    });
    if (JSON.stringify(flushedPrepared) === JSON.stringify(strippedPrepared)) {
      return null;
    }
    const textAreaPatch: Record<string, unknown> = {};
    for (const key of TEXT_AREA_TRANSPORT_FIELD_KEYS) {
      if (flushedPrepared[key] !== strippedPrepared[key]) {
        textAreaPatch[key] = flushedPrepared[key];
      }
    }
    if (Object.keys(textAreaPatch).length === 0) {
      return null;
    }
    return RequestsService.updateRequest(
      String(requestId),
      textAreaPatch as UpdateRequestData,
      options,
    );
  }

  /**
   * Create request - queues offline when no connection, creates immediately when online
   */
  async createRequest(
    data: CreateRequestData,
  ): Promise<Request | { offlineId: string; isOffline: true }> {
    const isConnected = networkService.isConnected();
    const preparedData = await this.prepareForTransport(
      data as unknown as Record<string, unknown>,
    );
    const typedPrepared = preparedData as unknown as CreateRequestData;

    // Neither team members nor deliverables are columns on the requests table, so both have
    // to be split out and handed to the queue as sub-objects. Queuing `data` whole would
    // drop them at replay time, which is what the single-argument calls here used to do.
    const queueOffline = (): Promise<string> => {
      const {
        vertivSponsors,
        vertivContributors,
        partnerContributors,
        deliverables,
        ...requestDataWithoutSubObjects
      } = typedPrepared as any;

      return offlineCache.addToOfflineQueue(
        requestDataWithoutSubObjects,
        {
          vertivSponsors,
          vertivContributors,
          partnerContributors,
          useDefaultSponsors: (typedPrepared as any).useDefaultSponsors,
          region: (typedPrepared as any).region,
        },
        deliverables,
      );
    };

    if (isConnected) {
      try {
        const request = await RequestsService.createRequest(typedPrepared);

        try {
          const flushedPrepared = await this.prepareForTransport(
            data as unknown as Record<string, unknown>,
            request.id,
          );
          if (
            JSON.stringify(flushedPrepared) !== JSON.stringify(preparedData)
          ) {
            const updated = await RequestsService.updateRequest(
              String(request.id),
              flushedPrepared as UpdateRequestData,
            );
            const { requests } = await offlineCache.getCachedRequests();
            const rest = requests.filter(item => item.id !== request.id);
            await offlineCache.cacheRequests([updated, ...rest]);
            return updated;
          }
        } catch (flushError) {
          console.warn(
            'Failed to attach staged inline images after create',
            flushError,
          );
        }

        const { requests } = await offlineCache.getCachedRequests();
        const updatedRequests = [request, ...requests];
        await offlineCache.cacheRequests(updatedRequests);

        return request;
      } catch (error) {
        console.warn('Failed to create request online, queuing offline', error);
        const offlineId = await queueOffline();
        return { offlineId, isOffline: true };
      }
    } else {
      const offlineId = await queueOffline();
      return { offlineId, isOffline: true };
    }
  }

  /**
   * Update request - works offline and online
   * Supports updating offline requests (o-*) before they sync
   * Implements "last write wins" for conflict resolution
   */
  async updateRequest(
    id: string,
    data: UpdateRequestData,
  ): Promise<Request | null> {
    const isConnected = networkService.isConnected();
    const isOfflineId = id.startsWith('o-');
    const preparedData = await this.prepareForTransport(
      data as unknown as Record<string, unknown>,
      id,
    );
    const typedPrepared = preparedData as unknown as UpdateRequestData;

    // Handle offline request updates (requests not yet synced)
    if (isOfflineId) {
      try {
        // Extract team member data if present
        const teamMemberData = {
          vertivSponsors: (preparedData as any).vertivSponsors,
          vertivContributors: (preparedData as any).vertivContributors,
          partnerContributors: (preparedData as any).partnerContributors,
          useDefaultSponsors: (preparedData as any).useDefaultSponsors,
          region: (preparedData as any).region,
        };

        // Remove team member fields and deliverables from request data. Neither is a
        // column on the requests table, so both must ride the queue as sub-objects rather
        // than reach the PATCH payload on replay.
        const {
          vertivSponsors,
          vertivContributors,
          partnerContributors,
          deliverables,
          ...requestDataWithoutTeam
        } = preparedData as any;

        // Update the offline request in the queue
        await offlineCache.updateOfflineRequest(
          id,
          requestDataWithoutTeam,
          teamMemberData,
          deliverables,
        );

        // Fetch the updated offline request and return as Request object
        const updatedOfflineRequest = await offlineCache.getOfflineRequestById(
          id,
        );

        if (!updatedOfflineRequest) {
          console.error('❌ Failed to retrieve updated offline request');
          return null;
        }

        const updatedRequest: Request = {
          ...updatedOfflineRequest.data,
          id: updatedOfflineRequest.id,
          customerName: updatedOfflineRequest.data.customerName,
          requestorName: updatedOfflineRequest.data.requestorName,
          region: updatedOfflineRequest.data.region,
          requestType: updatedOfflineRequest.data.requestType,
          stage: updatedOfflineRequest.data.stage || 'Initial',
          description: updatedOfflineRequest.data.description,
          additionalDetails: updatedOfflineRequest.data.additionalDetails || '',
          dateCreated: new Date(updatedOfflineRequest.timestamp).toISOString(),
          modifiedOn: new Date(updatedOfflineRequest.timestamp).toISOString(),
          modifiedBy: 'Offline (Updated)',
        };

        return updatedRequest;
      } catch (error) {
        console.error('❌ Failed to update offline request:', error);
        throw error;
      }
    }

    // Handle regular server request updates
    if (!isConnected) {
      // Offline mode - queue the update for later sync
      try {
        const {
          vertivSponsors,
          vertivContributors,
          partnerContributors,
          deliverables,
          ...requestDataWithoutSubObjects
        } = typedPrepared as any;

        await offlineCache.addToOfflineUpdateQueue(
          id,
          requestDataWithoutSubObjects,
          {
            vertivSponsors,
            vertivContributors,
            partnerContributors,
            useDefaultSponsors: (typedPrepared as any).useDefaultSponsors,
            region: (typedPrepared as any).region,
          },
          deliverables,
        );

        // Return the updated cached version for immediate UI update
        const cachedRequest = await offlineCache.getCachedRequestById(id);
        if (!cachedRequest) {
          console.error('❌ No cached request found for offline update');
          return null;
        }

        // Merge the update data with the cached request for immediate UI feedback
        const updatedRequest = {
          ...cachedRequest,
          ...typedPrepared,
        };

        // Update the cache with the merged data so it's reflected in the list
        const { requests } = await offlineCache.getCachedRequests();
        const updatedRequests = requests.map(req =>
          req.id === id ? updatedRequest : req,
        );
        await offlineCache.cacheRequests(updatedRequests);

        return updatedRequest;
      } catch (error) {
        console.error('❌ Failed to queue offline update:', error);
        throw error;
      }
    }

    try {
      const updatedRequest = await RequestsService.updateRequest(
        id,
        typedPrepared,
      );

      // Update in cache
      const { requests } = await offlineCache.getCachedRequests();
      const updatedRequests = requests.map(req =>
        req.id === id ? updatedRequest : req,
      );
      await offlineCache.cacheRequests(updatedRequests);

      return updatedRequest;
    } catch (error) {
      console.error('❌ Failed to update request:', error);
      throw error;
    }
  }

  /**
   * Delete request - only works when online
   */
  async deleteRequest(id: string): Promise<void> {
    if (!networkService.isConnected()) {
      throw new Error('Cannot delete requests while offline');
    }

    try {
      await RequestsService.deleteRequest(id);

      // Remove from cache
      const { requests } = await offlineCache.getCachedRequests();
      const filteredRequests = requests.filter(req => req.id !== id);
      await offlineCache.cacheRequests(filteredRequests);
    } catch (error) {
      console.error('Failed to delete request:', error);
      throw error;
    }
  }

  /**
   * Get offline-specific data and statistics
   */
  async getOfflineStats() {
    return await offlineCache.getCacheStats();
  }

  /**
   * Check if the app is currently offline
   */
  isOffline(): boolean {
    return !networkService.isConnected();
  }

  /**
   * Get cached requests metadata
   */
  async getCacheInfo() {
    const { metadata } = await offlineCache.getCachedRequests();
    return metadata;
  }
}

export const offlineRequestsService = new OfflineRequestsService();
